import React from 'react';
// 1. Import motion for hero section animations
import { motion } from 'framer-motion';

// 2. Define animation variants for the hero section
const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const sentenceVariant = {
  hidden: {},
  visible: {
    transition: {
      delayChildren: 0.3,
      staggerChildren: 0.08,
    },
  },
};

const wordVariant = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 100 } },
};

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

export default function TermsConditions() {
  const navLinks = [
    { href: "#acceptance", text: "Acceptance of Terms" },
    { href: "#eligibility", text: "Service Eligibility" },
    { href: "#responsibilities", text: "User Responsibilities" },
    { href: "#prohibited", text: "Prohibited Conduct" },
    { href: "#intellectual-property", text: "Intellectual Property" },
    { href: "#termination", text: "Termination" },
    { href: "#warranties", text: "Disclaimer of Warranties" },
    { href: "#liability", text: "Limitation of Liability" },
    { href: "#governing-law", text: "Governing Law" },
    { href: "#modifications", text: "Modifications" },
    { href: "#contact-us", text: "Contact Us" },
  ];

  return (
    <main className="bg-white text-gray-900 font-sans antialiased overflow-x-hidden ">
      {/* Hero Section with Animations */}
      <section className="relative flex min-h-[40vh] sm:min-h-[50vh] items-center justify-center px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white pt-20">
        <div className="absolute inset-0 z-0 opacity-10">
          <svg className="w-full h-full" viewBox="0 0 1400 400" preserveAspectRatio="xMidYMid slice">
            <path fill="#16a085" d="M0,400L80,373.3C160,347,320,293,480,280C640,267,800,293,960,306.7C1120,320,1280,320,1360,320L1400,320L1400,0L1360,0C1280,0,1120,0,960,0C800,0,640,0,480,0C320,0,160,0,80,0L0,0Z" />
          </svg>
        </div>
        <motion.div 
          className="relative z-10 max-w-7xl mx-auto text-center"
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
        >
          <motion.h1 
            className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 mb-6 tracking-tight"
            variants={sentenceVariant}
          >
            <span className="block">
              {"Terms & Conditions".split(" ").map((word, i) => (
                <motion.span key={i} className="inline-block" variants={wordVariant}>{word} </motion.span>
              ))}
            </span>
            <span className="block text-[#16a085] mt-2">
              {"Our Commitment to Fair Use".split(" ").map((word, i) => (
                <motion.span key={i} className="inline-block" variants={wordVariant}>{word} </motion.span>
              ))}
            </span>
          </motion.h1>
          <motion.p 
            variants={fadeInUp} 
            className="mt-4 max-w-3xl mx-auto text-base sm:text-lg text-gray-600 leading-relaxed"
          >
            These Terms & Conditions define your rights and responsibilities while using Void Support System. By accessing our services, you accept these terms in full.
          </motion.p>
        </motion.div>
      </section>

      {/* Terms Content (Static - No Animations) */}
      <div className="max-w-7xl xl:max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
          
          {/* Sidebar Navigation */}
          <aside className="lg:w-64 flex-shrink-0 lg:sticky lg:top-24 self-start">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
              Table of Contents
            </h2>
            <div className="lg:hidden flex overflow-x-auto scrollbar-hide -mx-4 px-4 pb-4 space-x-2">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="block flex-shrink-0 whitespace-nowrap px-3 py-2 text-sm font-medium text-gray-700 hover:text-[#16a085] bg-gray-50 hover:bg-gray-100 rounded-md transition-colors duration-150"
                >
                  {link.text}
                </a>
              ))}
            </div>
            <ul className="hidden lg:block space-y-2">
              {navLinks.map((link) => (
                <li key={link.href}>
                  <a href={link.href} className="block px-3 py-2 text-sm font-medium text-gray-700 hover:text-[#16a085] hover:bg-gray-50 rounded-md transition-colors duration-150">
                    {link.text}
                  </a>
                </li>
              ))}
            </ul>
          </aside>

          {/* Main Content */}
          <div className="lg:flex-1 min-w-0">
            <div className="prose prose-lg max-w-none text-gray-700">
              <section id="acceptance" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 border-b border-gray-200 pb-4 mb-6">1. Acceptance of Terms</h2>
                <p>By using Void Support System ("Void", "we", "our", or "us"), you agree to be bound by these Terms & Conditions. If you do not agree to any of these terms, please refrain from using our services.</p>
              </section>

              <section id="eligibility" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 border-b border-gray-200 pb-4 mb-6">2. Service Eligibility</h2>
                <p>You must be at least 16 years old and have the legal capacity to enter into binding agreements. By registering or using our platform, you confirm that you meet these requirements.</p>
              </section>

              <section id="responsibilities" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 border-b border-gray-200 pb-4 mb-6">3. User Responsibilities</h2>
                <p>You agree to:</p>
                <ul className="mt-4 space-y-3 list-disc list-inside">
                  <li>Maintain the confidentiality of your login credentials.</li>
                  <li>Provide accurate, up-to-date information during registration and use.</li>
                  <li>Not use our services for any illegal or harmful activities.</li>
                </ul>
              </section>

              <section id="prohibited" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 border-b border-gray-200 pb-4 mb-6">4. Prohibited Conduct</h2>
                <p>You agree not to:</p>
                <ul className="mt-4 space-y-3 list-disc list-inside">
                  <li>Interfere with or disrupt our servers or networks.</li>
                  <li>Reverse-engineer, decompile, or copy any part of the platform.</li>
                  <li>Upload malicious code, spam, or violate third-party rights.</li>
                </ul>
              </section>

              <section id="intellectual-property" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 border-b border-gray-200 pb-4 mb-6">5. Intellectual Property</h2>
                <p>All content, graphics, and software are owned by Void or its licensors. Unauthorized use or reproduction of our assets is strictly prohibited.</p>
              </section>

              <section id="termination" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 border-b border-gray-200 pb-4 mb-6">6. Termination</h2>
                <p>We reserve the right to suspend or terminate access to our platform without notice if these terms are violated or suspicious activity is detected.</p>
              </section>

              <section id="warranties" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 border-b border-gray-200 pb-4 mb-6">7. Disclaimer of Warranties</h2>
                <p>Void is provided on an "as is" and "as available" basis. We do not guarantee uninterrupted access or that our services will meet your expectations.</p>
              </section>

              <section id="liability" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 border-b border-gray-200 pb-4 mb-6">8. Limitation of Liability</h2>
                <p>We are not liable for any indirect, incidental, or consequential damages arising from the use or inability to use our services.</p>
              </section>

              <section id="governing-law" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 border-b border-gray-200 pb-4 mb-6">9. Governing Law</h2>
                <p>These terms are governed by the laws of the Republic of Kosovo, and any disputes shall be resolved by the competent courts in Ferizaj District.</p>
              </section>

              <section id="modifications" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 border-b border-gray-200 pb-4 mb-6">10. Modifications</h2>
                <p>We reserve the right to update these Terms & Conditions at any time. Continued use of our services after changes indicates acceptance of the revised terms.</p>
              </section>

              <section id="contact-us" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 border-b border-gray-200 pb-4 mb-6">11. Contact Us</h2>
                <p>If you have any questions regarding these Terms & Conditions, please contact us:</p>
                <div className="mt-6 space-y-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="h-6 w-6 text-[#16a085]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">Email</p>
                      <a href="mailto:contact@shigjeta.com" className="text-sm text-[#16a085] hover:underline">contact@shigjeta.com</a>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="h-6 w-6 text-[#16a085]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">Address</p>
                      <p className="text-sm text-gray-700">Void Support System HQ, Kacanik, Kosovo</p>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}